import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";
import { entityOfType } from "../../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";
import {
  DEFAULT_SUPPLIER_DOCUMENT_MATCH_FIELD_NAMES,
  supplierDocumentLineMatchSchema,
} from "./supplier-document-line-match";

/**
 * Match Supplier Document Lines
 *
 * Read-only. Takes a PDF (CORE_DOCUMENT) or inbound email plus a supplier,
 * extracts table rows with a pinned stored schema, and matches each row to:
 *   1. that supplier's SupplierProduct translation catalog (SKU / UPC /
 *      freeform — the vendor analog of CustomerProduct)
 *   2. the org ProductV2 / ProductVariant catalog (ProductMatchingService)
 *
 * Extra per-row columns (quantity, pack size, …) come from the pinned
 * schema and ride along on each line's `extracted` object. Nothing is
 * persisted; wire `writeCandidates` (or `matchedLines`) into
 * `write_supplier_product_matches` to learn translations.
 */
export const matchSupplierDocumentLinesStep = defineStepConfig({
  type: "match_supplier_document_lines",
  name: "Match Supplier Document Lines",
  description:
    "Extract line items from a supplier PDF or inbound email and match them to SupplierProduct translations, then the internal product catalog. Read-only — extra schema fields (quantity, price, …) are included on each line.",
  category: "Transformation",
  libraryModule: "AccountsPayable",

  inputSchema: z.object({
    supplier: entityOfType(SystemEntity.AP_SUPPLIER).describe(
      "Supplier whose product translations and catalog should be searched",
    ),
    document: entityOfType(SystemEntity.CORE_DOCUMENT)
      .optional()
      .describe(
        "PDF (or other extractable document) to pull line items from. Required when inboundEmail is not wired.",
      ),
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL)
      .optional()
      .describe(
        "Inbound email whose attached documents should be extracted. Used when document is not wired; ignored when document is present.",
      ),
  }),

  outputSchema: z.object({
    lines: z
      .array(supplierDocumentLineMatchSchema)
      .describe("Every extracted row, matched or not"),
    matchedLines: z
      .array(supplierDocumentLineMatchSchema)
      .describe("Rows that resolved to a product variant"),
    unmatchedLines: z
      .array(supplierDocumentLineMatchSchema)
      .describe("Rows that did not resolve to a product"),
    writeCandidates: z
      .array(supplierDocumentLineMatchSchema)
      .describe(
        "Catalog matches with no existing SupplierProduct — the usual input to write_supplier_product_matches",
      ),
    lineCount: z.number().int().nonnegative(),
    matchedCount: z.number().int().nonnegative(),
    unmatchedCount: z.number().int().nonnegative(),
    writeCandidateCount: z.number().int().nonnegative(),
    lowestConfidence: z
      .number()
      .min(0)
      .max(1)
      .describe(
        "Lowest confidence among matched rows. 0 when every row is unmatched; 1 when there are no rows.",
      ),
    supplier: entityOfType(SystemEntity.AP_SUPPLIER),
  }),

  configSchema: z.object({
    extractionSchemaDefinitionId: z
      .uuid()
      .describe(
        "Stored extraction schema whose fields describe each line (identity plus extras such as quantity)",
      ),
    extractionSchemaDefinitionVersionId: z
      .uuid()
      .optional()
      .describe(
        "Pinned schema version. Omit to track the schema's active version.",
      ),
    lineItemsFieldName: z
      .string()
      .min(1)
      .optional()
      .describe(
        "Top-level array field on the extracted payload that holds the rows. When omitted, uses `lineItems`, or the sole array field, or the whole object as a single row.",
      ),
    matchFieldNames: z
      .array(z.string().min(1))
      .prefault([...DEFAULT_SUPPLIER_DOCUMENT_MATCH_FIELD_NAMES])
      .describe(
        "Extracted field names used as match candidates, in priority order",
      ),
    enableCatalogFallback: z
      .boolean()
      .prefault(true)
      .describe(
        "When no SupplierProduct hits, fall back to org-wide ProductV2 / ProductVariant matching",
      ),
    minCatalogConfidence: z
      .number()
      .min(0)
      .max(1)
      .prefault(0.7)
      .describe(
        "Minimum ProductMatchingService confidence required to accept a catalog fallback match",
      ),
    findVectorMatches: z
      .boolean()
      .prefault(true)
      .describe(
        "Allow semantic vector search in the catalog fallback (exact SKU still runs first)",
      ),
    persistOcrResult: z
      .boolean()
      .prefault(true)
      .describe(
        "Persist a CORE_DOCUMENT_OCR_RESULT row after a fresh extraction",
      ),
    reuseExistingOcrResult: z
      .boolean()
      .prefault(true)
      .describe(
        "Reuse a matching completed OCR result instead of calling the provider again",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "15 minutes",
    retry: {
      maximumAttempts: 2,
      initialInterval: "10 seconds",
      backoffCoefficient: 2,
    },
  },
});
